import { Logger, LoggerConstant } from '@/hooks/useLogging';
import { authHttpClient, httpClient, internalAuthHttpClient } from '@/libs/axios';
import { kyc3Axios } from '@/libs/axios/kyc3Client';
import userReducer from '@/redux/reducers/userReducer';
import { getAccessToken } from '@/utils/auth';

interface CustomerInfoRes {
  user_id: string;
  last_name: string;
  first_name: string;
}

interface CustomerInfoCP {
  custom_token: string;
  first_name: string;
  email: string;
  partner_code: string;
}

interface CustomerInfoDetailCP {
  customer_id: string;
  last_name: string;
  first_name: string;
  phone_number: string;
  phone_number_status: string;
  email: string;
}

export const getCusNameByOrderId = async (orderId: string | undefined): Promise<CustomerInfoRes | undefined> => {
  try {
    const response = await internalAuthHttpClient.get(`/order/${orderId}/user`);
    return await response.data.data;
  } catch (error) {
    window.location.href = '/404';
  }
};

export const updateUser = async (uid: string | undefined, payload: any): Promise<any> => {
  try {
    const response = await authHttpClient.patch(`users/${uid}`, payload);
    const data = await response.data;
    return data;
  } catch (error: any) {
    throw error.error;
    //define logging
  }
};

export const reloadUser = async (uid: string | undefined): Promise<any> => {
  try {
    const response = await authHttpClient.get(`users/${uid}`);
    const data = await response.data;
    return data;
  } catch (error: any) {
    throw error.error;
    //define logging
  }
};

export const getStep = async (uid: string | undefined, dispatch: any): Promise<any> => {
  try {
    const kyc_token = localStorage.getItem('kyc_token');
    const kyc_id = localStorage.getItem('kyc_id');
    if (!kyc_token || !kyc_id) {
      return;
    }
    const { kycStep, kyc } = await getKYCSummary(kyc_id, kyc_token, uid);
    dispatch(userReducer.actions.updateUserKYCResult({ kycStep, kyc }));
    const kycPassed = kyc.stage === 'Completed';
    return {
      verify_identity_passed: kycPassed,
    };
  } catch (error: any) {
    throw error.error;
    //define logging
  }
};
export const checkKYC = async (dispatch: any, uid: string) => {
  try {
    const kyc_token = localStorage.getItem('kyc_token');
    const kyc_id = localStorage.getItem('kyc_id');
    if (!kyc_token || !kyc_id) {
      return false;
    }
    const { kycStep, kyc } = await getKYCSummary(kyc_id, kyc_token, uid);
    dispatch(userReducer.actions.updateUserKYCResult({ kycStep, kyc }));
    const kycPassed = kyc.stage === 'Completed';
    return kycPassed;
  } catch (error: any) {
    window.location.href = '/404';
    //define logging
  }
};

export const updateUserSelfie = async (
  uid: string | undefined,
  variant: string | undefined,
  id: string | undefined,
): Promise<any> => {
  try {
    const formData = {
      id,
    };
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Auth-Token': getAccessToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    };
    const url = `${process.env.REACT_APP_API_URL}/users/${uid}/documents/selfie${variant === 'video' ? '/video' : ''}`;
    //@ts-ignore
    const response = await fetch(url, options);
    return response;
  } catch (error: any) {
    throw error.error;
    //define logging
  }
};

export const uploadPassport = async ({ userId, passport }: any) => {
  try {
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Auth-Token': getAccessToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ passport }),
    };
    const url = `${process.env.REACT_APP_API_URL}/users/${userId}/documents/passport`;
    //@ts-ignore
    const response = await fetch(url, options);

    return response;
  } catch (error: any) {
    throw error.error;
  }
};

export const getUserInfo = async (uid: string | undefined): Promise<any> => {
  try {
    const response = await authHttpClient.get(`users/${uid}`);
    const data = await response.data;
    return data;
  } catch (error: any) {
    throw error.error;
    //define logging
  }
};

export const getTokenOnfido = async (uid: string) => {
  try {
    const url = `${process.env.REACT_APP_API_URL}/users/${uid}/onfido`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest ',
        'X-Auth-Token': `${getAccessToken()}`,
      },
    });
    if (!!!response.ok) {
      throw response;
    }
    return response.json();
  } catch (error) {
    throw error;
  }
};

export const getTokenKYC3 = async (uid: string) => {
  try {
    const response = await kyc3Axios.post('/kyc/on-boarding', {
      token: `${getAccessToken()}`,
    });
    Logger.info({
      module: LoggerConstant.KYC3,
      funcName: 'Generate KYC Id and Token Success',
      params: { response: response.data, userId: uid },
    });
    return response.data;
  } catch (error) {
    Logger.error({
      module: LoggerConstant.KYC3,
      funcName: 'Generate KYC Id and Token Fail',
      error: { error, userId: uid },
    });
    throw error;
  }
};

export const sendConsentKyc3 = async (uid: string) => {
  try {
    const kyc_token = localStorage.getItem('kyc_token');
    const kyc_id = localStorage.getItem('kyc_id');
    if (!kyc_token || !kyc_id) {
      return;
    }
    const response = await kyc3Axios.patch(
      `/insurance-check/v3/consent/${kyc_id}`,
      {
        consent: true,
        effectiveFrom: new Date().toISOString(),
        source: window.location.href,
      },
      {
        headers: {
          Authorization: `${kyc_token}`,
        },
      },
    );
    Logger.info({
      module: LoggerConstant.KYC3,
      funcName: 'Send Consent KYC3 Success',
      params: { response: response.data, userId: uid },
    });
    sessionStorage.setItem(
      'consent_checked',
      JSON.stringify([...JSON.parse(sessionStorage.getItem('consent_checked') || '[]'), uid]),
    );
    return response.data;
  } catch (error) {
    Logger.error({
      module: LoggerConstant.KYC3,
      funcName: 'Send Consent KYC3 Fail',
      error: { error, userId: uid },
    });
    throw error;
  }
};

export const getKYCSummary = async (kycId: string | null, token: string | null, uid: string | undefined) => {
  try {
    const response = await kyc3Axios.get(`/personal-details/v3/${kycId}/summary`, {
      headers: {
        Authorization: `${token}`,
      },
    });
    Logger.info({
      module: LoggerConstant.KYC3,
      funcName: 'Get KYC Result Success',
      params: { ...response.data, kycId, userId: uid },
    });
    return response.data;
  } catch (error) {
    Logger.error({
      module: LoggerConstant.KYC3,
      funcName: 'Get KYC Result Fail',
      error: { error, kycId, userId: uid },
    });
    throw error;
  }
};

export const uploadLicence = async ({ userId, ...payload }: any) => {
  const url = `/users/${userId}/documents/licence`;
  const response = await authHttpClient.post(url, payload);
  return response;
};

export const getCusInfoByPartnerIdAndCusId = async (
  partnerId: string | undefined,
  cusId: string | undefined,
): Promise<CustomerInfoCP | undefined> => {
  try {
    const response = await internalAuthHttpClient.get(`/partner/${partnerId}/user/${cusId}`);
    return await response.data.data;
  } catch (error) {
    window.location.href = '/404';
  }
};

export const getCusInfoByToken = async (token: string | null): Promise<CustomerInfoCP | undefined | null> => {
  try {
    const response = await authHttpClient.post('/checkin/verify', { token: token });
    return await response.data;
  } catch (error) {
    return null;
    // window.location.href = '/404';
  }
};

export const getCustomerInfoCP = async (cusId: string | undefined): Promise<CustomerInfoDetailCP | undefined> => {
  try {
    const response = await authHttpClient.get(`/users/partner/${cusId}`);
    return await response.data.data;
  } catch (error: any) {
    throw error.error;
  }
};

export const checkPhoneNumber = async (phoneNumber: string | undefined): Promise<any> => {
  try {
    const response = await httpClient.get(`/users?phone_number=${phoneNumber}`);
    const data = await response.data;
    return data;
  } catch (error: any) {
    throw error.error;
  }
};

export const updatePrivacy = async (
  cusId: string | undefined,
  payload: any,
): Promise<CustomerInfoDetailCP | undefined> => {
  try {
    const response = await authHttpClient.patch(`/users/${cusId}/checkin`, payload);
    const data = await response.data;
    return data;
  } catch (error: any) {
    throw error.error;
  }
};

export const verifyAdditionalDriver = async (userId: string, orderId: string) => {
  const response = await authHttpClient.post(`/users/${userId}/orders/${orderId}/additional-drivers`);
  return response.data;
};
